import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ButtonsGroupSingleSelect } from '../../shared/components/ButtonsGroupSingleSelect';
import { OllieToast, OllieToastRef } from '../../shared/components/OllieToast';
import { TitleAndSubtitle } from '../../shared/components/TitleAndSubtitle';
import { useLoggedInUser } from '../../shared/hooks/useLoggedInUser';
import { useLoggedInUserEdit } from '../../shared/hooks/useLoggedInUserEdit';
import { ROUTES } from '../../shared/routing/routes';
import useOcdProfileStore from '../../systems/user-info/useOcdProfileStore';
import { OnboardingProgress } from '../../types/obsessless-user';
import { useOnboardingStore } from './hooks/useOnboardingStore';
import { useOnboardingOcdDetailsStore } from './hooks/useOnboardingOcdDetailsStore';
import { useOnboardingOcdThemesStore } from './hooks/useOnboardingOcdThemesStore';

export const OcdThemesDetailsSelect = () => {
    const navigate = useNavigate();
    const user = useLoggedInUser();
    const ocdProfile = useOcdProfileStore(state => state.ocdProfile);
    const saveOcdInfoFromUser = useOcdProfileStore(state => state.saveOcdInfoFromUser);
    const setCurrentStep = useOnboardingStore(state => state.setCurrentStep);
    const initOcdThemes = useOnboardingOcdThemesStore(state => state.init);
    const init = useOnboardingOcdDetailsStore(state => state.init);
    const generateNextOnboardingQuestion = useOnboardingOcdDetailsStore(state => state.generateNextOnboardingQuestion);
    const currentQuestion = useOnboardingOcdDetailsStore(state => state.currentQuestion);
    const hasMoreQuestions = useOnboardingOcdDetailsStore(state => state.hasMoreQuestions);
    const currentInternalStep = useOnboardingOcdDetailsStore(state => state.currentStep);
    const [isLoading, setIsLoading] = useState(false);
    const toastRef = useRef<OllieToastRef>(null);
    const { saveOnboardingProgress } = useLoggedInUserEdit();

    const onboardingStep = 7;
    useEffect(() => {
        setCurrentStep(onboardingStep + currentInternalStep);
    }, [setCurrentStep, currentInternalStep]);

    // Get user's selected OCD themes
    const userOcdThemes = useMemo(() => {
        return ocdProfile?.ocdInfos['ocd_types']?.userProvided || [];
    }, [ocdProfile]);

    // Initialize OCD themes store first
    useEffect(() => {
        initOcdThemes();
    }, [initOcdThemes]);

    // Initialize with user's OCD themes to show theme selection question
    useEffect(() => {
        if (userOcdThemes.length > 0 && currentInternalStep === -1) {
            init(userOcdThemes);
        }
    }, [userOcdThemes, currentInternalStep, init]);


    const handleSelect = async (value: string) => {
        setIsLoading(true);

        try {
            const showToast = toastRef.current?.animateIn(true);
            const saveData = saveOcdInfoFromUser(user.uid, currentQuestion.ocdProfileKey, [value]);
            const generateQuestion = hasMoreQuestions ? generateNextOnboardingQuestion(value) : Promise.resolve();
            const onboardingProgressPromise = !hasMoreQuestions ? saveOnboardingProgress(OnboardingProgress.AnsweredOcdThemeQuestions) : Promise.resolve();
            await Promise.all([showToast, saveData, generateQuestion, onboardingProgressPromise]);
        }
        finally {
            await toastRef.current?.animateOut();
            setIsLoading(false);
        }

        if (!hasMoreQuestions) {
            navigate(ROUTES.onboarding.personalizingOllie);
        }
    }

    // Don't render until we have a valid question
    if (!currentQuestion.question || currentQuestion.options.length === 0) {
        return null;
    }

    return (
        <div className='flex flex-col justify-between gap-4 safe-pb-4 px-8 flex-1 overflow-hidden'>
            <title>Ocd Themes Details</title>
            <OllieToast ref={toastRef} text='Learning' animateOutDelay={1000} />
            <AnimatePresence mode='wait'>
                <motion.div
                    key={currentQuestion.question}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className='flex flex-col justify-between gap-4 flex-1 overflow-hidden'
                >
                    <motion.div
                        className='flex-shrink-0'
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3, delay: 0 }}
                    >
                        <TitleAndSubtitle
                            title={currentQuestion.question}
                            subtitle={currentQuestion.subtitle} />
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3, delay: 0.8 }}
                    >
                        <ButtonsGroupSingleSelect variant='outlined' disabled={isLoading} options={currentQuestion.options} onSelect={handleSelect} />
                    </motion.div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
};